import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Trash2, Download, ArrowLeft } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { composeStickerWithPhrase, downloadDataUrl } from "@/lib/sticker";
import { useUnlock } from "@/lib/payments";
import PaywallModal from "@/components/PaywallModal";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composed, setComposed] = useState({}); // id -> dataUrl
  const [paywallOpen, setPaywallOpen] = useState(false);
  const { unlocked, checking } = useUnlock();

  const load = async () => {
    if (!unlocked) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data || []);
    } catch (e) {
      toast.error("No se pudo cargar la galería");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  useEffect(() => {
    // pre-compose all cards
    (async () => {
      const map = {};
      for (const it of items) {
        try {
          map[it.id] = await composeStickerWithPhrase(it.image_base64, it.phrase);
        } catch (e) {
          // skip
        }
      }
      setComposed(map);
    })();
  }, [items]);

  const del = async (id) => {
    try {
      await axios.delete(`${API}/stickers/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Sticker borrado");
    } catch {
      toast.error("No se pudo borrar");
    }
  };

  const dl = (id) => {
    const url = composed[id];
    if (!url) return;
    if (!unlocked) {
      setPaywallOpen(true);
      return;
    }
    downloadDataUrl(url, `caliche-${id}.png`);
  };

  return (
    <div className="pt-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-anton uppercase text-4xl sm:text-5xl">
          Tu <span className="text-[#006847]">galería</span> chingona
        </h1>
        <Link
          to="/"
          className="nb-btn font-anton uppercase text-sm px-3 py-2 bg-white border-[3px] border-black shadow-[3px_3px_0_0_#000] inline-flex items-center gap-2"
        >
          <ArrowLeft strokeWidth={3} className="w-4 h-4" /> Crear otro
        </Link>
      </div>

      {loading && (
        <div className="font-anton uppercase text-2xl">Cargando…</div>
      )}

      {!checking && !unlocked && (
        <div
          data-testid="gallery-locked"
          className="border-[4px] border-black shadow-[6px_6px_0_0_#000] bg-white p-8 text-center"
        >
          <div className="inline-block bg-[#FFD100] border-[3px] border-black shadow-[3px_3px_0_0_#000] px-3 py-1 rotate-[-2deg] mb-4">
            <span className="font-anton uppercase text-sm">🔒 Galería bloqueada</span>
          </div>
          <h2 className="font-anton uppercase text-3xl sm:text-4xl">Sólo pa&apos; chingones con pase</h2>
          <p className="font-outfit text-black/70 mt-2 max-w-md mx-auto">
            La galería es exclusiva para quienes ya desbloquearon el pase. Paga una vez $25 MXN y accede a todos tus stickers para siempre en este dispositivo.
          </p>
          <button
            onClick={() => setPaywallOpen(true)}
            data-testid="gallery-unlock-cta"
            className="mt-6 nb-btn font-anton uppercase text-xl px-6 py-3 bg-[#CE1126] text-white border-[3px] border-black shadow-[5px_5px_0_0_#000]"
          >
            Desbloquear por $25 MXN
          </button>
        </div>
      )}

      {!loading && unlocked && items.length === 0 && (
        <div
          data-testid={TEST_IDS.galleryEmpty}
          className="border-[3px] border-black shadow-[5px_5px_0_0_#000] bg-white p-8 text-center"
        >
          <h2 className="font-anton uppercase text-3xl">¡Vacío como estadio en lunes!</h2>
          <p className="font-outfit text-black/70 mt-2">
            Aún no has creado stickers. Ve a la portada y sácate el primero.
          </p>
          <Link
            to="/"
            className="mt-4 inline-block nb-btn font-anton uppercase px-5 py-3 bg-[#CE1126] text-white border-[3px] border-black shadow-[4px_4px_0_0_#000]"
          >
            Crear mi sticker
          </Link>
        </div>
      )}

      {!loading && unlocked && items.length > 0 && (
        <>
        {!unlocked && (
          <div className="mb-5 bg-[#FFD100] border-[3px] border-black shadow-[4px_4px_0_0_#000] p-3 flex items-center justify-between gap-3">
            <span className="font-anton uppercase text-sm sm:text-base">
              🔒 Descarga desbloqueada con pase de $25 MXN
            </span>
            <button
              onClick={() => setPaywallOpen(true)}
              className="nb-btn font-anton uppercase text-xs px-3 py-2 bg-[#CE1126] text-white border-[3px] border-black shadow-[3px_3px_0_0_#000]"
              data-testid="gallery-unlock-btn"
            >
              Desbloquear
            </button>
          </div>
        )}
        <div
          data-testid={TEST_IDS.galleryGrid}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={TEST_IDS.galleryItem}
              className="bg-white border-[3px] border-black shadow-[5px_5px_0_0_#000] p-2"
            >
              <div className="aspect-square border-[3px] border-black overflow-hidden bg-[#FDFBF7]">
                {composed[it.id] ? (
                  <img src={composed[it.id]} alt={it.phrase} className="w-full h-full object-cover" />
                ) : (
                  <img
                    src={`data:image/png;base64,${it.image_base64}`}
                    alt={it.phrase}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="pt-3 pb-1 px-1">
                <p className="font-anton uppercase text-base leading-tight">“{it.phrase}”</p>
                <p className="font-outfit text-xs text-black/50 mt-1">
                  {new Date(it.created_at).toLocaleString()}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => dl(it.id)}
                    className="nb-btn font-anton uppercase text-xs px-3 py-2 bg-[#006847] text-white border-[3px] border-black shadow-[3px_3px_0_0_#000] inline-flex items-center gap-1.5"
                  >
                    <Download strokeWidth={3} className="w-4 h-4" /> Descargar
                  </button>
                  <button
                    onClick={() => del(it.id)}
                    className="nb-btn font-anton uppercase text-xs px-3 py-2 bg-white border-[3px] border-black shadow-[3px_3px_0_0_#000] inline-flex items-center gap-1.5"
                  >
                    <Trash2 strokeWidth={3} className="w-4 h-4" /> Borrar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} reason="Descargar" />
    </div>
  );
}
